import GenericTable, { IColumn } from "./Table";
import Pagination from "./Pagination";
import { CommonConfig } from "../../shared/Constants";
import { GridContainer } from "../Containers";
import { useEffect, useState } from "react";
import { ComponentStatus } from "..";
import { PaginatedResult } from "../../models";
import { ServiceResponse } from "../../shared";
import { FavaloroSpinner } from "../Spinner";
import { errorModal } from "../Alerts";

interface IGrid<T> {
    columns: IColumn<T>[];
    service: (page: number) => Promise<ServiceResponse<PaginatedResult<T>>>;
    forceRefresh?: boolean;
}

const GridFactory = <T extends any>() => {
    const Table = GenericTable<T>();
    const Instance: React.FC<IGrid<T>> = ({
        columns,
        service,
        forceRefresh,
    }) => {
        const [status, setStatus] = useState<ComponentStatus>("complete");
        const [error, setError] = useState<string>("");

        const [index, setIndex] = useState<number>(0);
        const [count, setCount] = useState<number>(0);
        const [items, setItems] = useState<T[]>([]);

        const fetchData = async (pageIndex: number) => {
            const sr = await service(pageIndex);
            if (sr.status()) setItems(sr.data.list);
            else {
                setStatus("errored");
                setError(sr.error);
            }
        };

        useEffect(() => {
            const fetch = async (index: number) => {
                setStatus("loading");
                const sr = await service(index);

                if (sr.status()) {
                    setItems(sr.data.list);
                    setCount(sr.data.count);
                    setStatus("complete");
                } else {
                    setStatus("errored");
                    setError(sr.error);
                }
            };

            fetch(0);
        }, [service, forceRefresh]);

        const handleIndex = (id: number) => {
            setIndex(id);
            fetchData(id);
        };

        let pages = Math.floor(count / CommonConfig.ITEMS_PER_PAGE);
        if (count % CommonConfig.ITEMS_PER_PAGE > 0) pages += 1;

        switch (status) {
            case "loading":
                return <FavaloroSpinner />;

            case "complete":
                return (
                    <GridContainer>
                        <Table items={items} columns={columns} />
                        <Pagination
                            pages={pages}
                            currentIndex={index}
                            handleIndex={handleIndex}
                        />
                    </GridContainer>
                );
            case "errored":
                return errorModal(error);
            default:
                return null;
        }
    };

    return Instance;
};

export default GridFactory;
